import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Download, PencilSimpleLine, PaperPlaneTilt, WhatsappLogo,
  EnvelopeSimple, Copy, Check, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MaterialPreviewModal — popup unificato per scaricare/modificare/inviare
 * un materiale già pronto (Brochure, Volantino, QR, Link pubblico, ZIP foto).
 *
 * Risolve il bug UX storico per cui cliccando "Volantino A5" senza la
 * pagina pubblica attiva ti ritrovavi su Promuovi senza spiegazione.
 *
 * Props:
 *   open, onClose
 *   title         · titolo grande del modal ("Volantino A5")
 *   subtitle      · es. "PDF · A5 fronte/retro con QR"
 *   previewUrl    · URL dell'anteprima (img/PDF). Optional.
 *   previewType   · "image" | "pdf" | "link". Default "image".
 *   needsSetup    · se true mostra warning + pulsante "Configura"
 *   setupLabel    · testo del pulsante "Configura" (es. "Rendi annuncio online")
 *   onSetup       · callback su click "Configura"
 *   onDownload    · callback "Scarica"
 *   onEdit        · callback "Modifica" (opzionale)
 *   shareUrl      · URL da inviare via WhatsApp/Email/Copy
 *   shareText     · testo del messaggio WhatsApp
 *   testId        · suffisso data-testid per il modal (default "material")
 */
export default function MaterialPreviewModal({
  open, onClose,
  title, subtitle,
  previewUrl, previewType = "image",
  needsSetup = false, setupLabel = "Configura prima", onSetup,
  onDownload, onEdit,
  shareUrl, shareText,
  testId = "material",
}) {
  if (!open) return null;

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid={`${testId}-preview-overlay`}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 10 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
          data-testid={`${testId}-preview-modal`}
        >
          {/* Header */}
          <div className="px-5 sm:px-7 pt-5 pb-4 border-b border-[#EAE7DF] flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
                Anteprima
              </div>
              <h3 className="font-editorial text-xl sm:text-2xl leading-tight text-[#1C1C1A] mt-0.5 truncate">
                {title}
              </h3>
              {subtitle && (
                <p className="text-[12px] text-[#6B6A65] mt-1">{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-[#F1F4ED] inline-flex items-center justify-center transition-colors"
              aria-label="Chiudi"
              data-testid={`${testId}-preview-close`}
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-5 sm:px-7 py-5">
            {needsSetup && (
              <div
                className="mb-4 p-4 bg-[#FFFAEC] border border-[#E8D9A8] rounded-lg"
                data-testid={`${testId}-needs-setup`}
              >
                <div className="text-[13px] font-semibold text-[#1C1C1A] mb-1">
                  Manca un passo prima
                </div>
                <p className="text-[12px] text-[#6B6A65] leading-snug mb-3">
                  Questo materiale richiede che l'annuncio sia online (perché contiene il QR o il link della pagina pubblica). Configura prima la pagina pubblica e poi torni qui per scaricare/inviare.
                </p>
                <button
                  onClick={() => { onClose(); onSetup?.(); }}
                  className="inline-flex items-center gap-1.5 bg-[#7C8B6E] hover:bg-black text-white text-[12.5px] font-semibold px-3 py-2 rounded-md transition-colors"
                  data-testid={`${testId}-setup-cta`}
                >
                  {setupLabel} →
                </button>
              </div>
            )}

            {/* Preview area */}
            {previewUrl && !needsSetup && (
              <div className="bg-[#FAFAF7] border border-[#EAE7DF] rounded-lg p-3 sm:p-4 flex items-center justify-center min-h-[260px] max-h-[440px] overflow-hidden">
                {previewType === "image" && (
                  <img
                    src={previewUrl}
                    alt={title}
                    className="max-h-[420px] w-auto rounded shadow-sm"
                    data-testid={`${testId}-preview-img`}
                  />
                )}
                {previewType === "pdf" && (
                  <iframe
                    src={previewUrl}
                    title={title}
                    className="w-full h-[420px] border-0 rounded"
                    data-testid={`${testId}-preview-pdf`}
                  />
                )}
                {previewType === "link" && (
                  <div className="text-center py-8">
                    <ArrowSquareOut size={28} weight="bold" className="mx-auto text-[#7C8B6E] mb-2" />
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[13px] font-mono text-[#1C1C1A] underline break-all"
                      data-testid={`${testId}-preview-link-url`}
                    >
                      {previewUrl}
                    </a>
                  </div>
                )}
              </div>
            )}

            {!previewUrl && !needsSetup && (
              <div className="text-center py-10 text-sm text-[#6B6A65]">
                Anteprima non disponibile — usa i pulsanti qui sotto.
              </div>
            )}
          </div>

          {/* Footer · 3 azioni */}
          {!needsSetup && (
            <div className="px-5 sm:px-7 py-4 border-t border-[#EAE7DF] bg-[#FAFAF7] flex flex-wrap items-center gap-2">
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[12.5px] font-semibold px-3.5 py-2.5 rounded-md transition-colors"
                  data-testid={`${testId}-action-download`}
                >
                  <Download size={13} weight="bold" /> Scarica
                </button>
              )}
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-1.5 bg-white text-[#1C1C1A] border border-[#E5E7E0] hover:border-[#7C8B6E] text-[12.5px] font-semibold px-3.5 py-2.5 rounded-md transition-colors"
                  data-testid={`${testId}-action-edit`}
                >
                  <PencilSimpleLine size={13} weight="bold" /> Modifica
                </button>
              )}
              {shareUrl && (
                <>
                  <div className="flex-1 min-w-0" />
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent((shareText || "") + " " + shareUrl)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1FB855] text-white text-[12.5px] font-semibold px-3 py-2.5 rounded-md transition-colors"
                    data-testid={`${testId}-action-whatsapp`}
                  >
                    <WhatsappLogo size={13} weight="bold" /> WhatsApp
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent((shareText || "") + "\n\n" + shareUrl)}`}
                    className="inline-flex items-center gap-1.5 bg-white text-[#1C1C1A] border border-[#E5E7E0] hover:border-[#7C8B6E] text-[12.5px] font-semibold px-3 py-2.5 rounded-md transition-colors"
                    data-testid={`${testId}-action-email`}
                  >
                    <EnvelopeSimple size={13} weight="bold" /> Email
                  </a>
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 bg-white text-[#1C1C1A] border border-[#E5E7E0] hover:border-[#7C8B6E] text-[12.5px] font-semibold px-3 py-2.5 rounded-md transition-colors"
                    data-testid={`${testId}-action-copy`}
                  >
                    <Copy size={13} weight="bold" /> Copia link
                  </button>
                </>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
